export interface TutorialStep {
  image: string;
  description: string;
}

export interface ExchangeCredentialCopy {
  keyLabel: string;
  keyPlaceholder: string;
  keyEmpty: string;
  secretLabel: string;
  secretPlaceholder: string;
  secretEmpty: string;
  showSecretNote: boolean;
  secretHint?: string;
  passphraseLabel: string;
  passphrasePlaceholder: string;
  passphraseEmpty: string;
  passphraseHint?: string;
  /** Wallet addresses stay visible. Secrets (KuCoin passphrase, private keys) stay masked. */
  maskPassphrase: boolean;
}

const DEFAULT_CREDENTIAL_COPY: ExchangeCredentialCopy = {
  keyLabel: "API Key",
  keyPlaceholder: "Enter your API Key",
  keyEmpty: "Please fill in the API Key",
  secretLabel: "API Secret",
  secretPlaceholder: "Enter your API Secret",
  secretEmpty: "Please fill in the API Secret",
  showSecretNote: true,
  passphraseLabel: "API Passphrase",
  passphrasePlaceholder: "Enter your API Passphrase",
  passphraseEmpty: "Please fill in the API Passphrase",
  maskPassphrase: true,
};

export const getExchangeCredentialCopy = (
  exchangeCode: string
): ExchangeCredentialCopy => {
  if (exchangeCode === "aster") {
    return {
      keyLabel: "API Wallet Address",
      keyPlaceholder: "Enter your API wallet address (0x...)",
      keyEmpty: "Please fill in the API Wallet Address",
      secretLabel: "API Wallet Private Key",
      secretPlaceholder: "Enter your API wallet private key",
      secretEmpty: "Please fill in the API Wallet Private Key",
      showSecretNote: false,
      secretHint:
        "In Authorise API, hover the private key and click Copy Private Key. It is not shown again after you authorise.",
      passphraseLabel: "Main Wallet Address",
      passphrasePlaceholder: "Enter the connected wallet from the top of the Aster page (0x...)",
      passphraseEmpty: "Please fill in the Main Wallet Address",
      passphraseHint:
        "This is the wallet connected at the top of the page, on the Main account tab. Not the API Wallet Address in the table.",
      maskPassphrase: false,
    };
  }

  if (exchangeCode === "hyperliquid") {
    return {
      keyLabel: "API Wallet Public Key",
      keyPlaceholder: "Enter your API Wallet public key (0x...)",
      keyEmpty: "Please fill in the API Wallet Public Key",
      secretLabel: "API Wallet Private Key",
      secretPlaceholder: "Enter your API Wallet private key",
      secretEmpty: "Please fill in the API Wallet Private Key",
      showSecretNote: false,
      passphraseLabel: "Account Wallet",
      passphrasePlaceholder: "Enter your Account Wallet address (0x...)",
      passphraseEmpty: "Please fill in the Account Wallet",
      passphraseHint:
        "This is the Ethereum wallet address you used to create your Hyperliquid account",
      maskPassphrase: false,
    };
  }

  return DEFAULT_CREDENTIAL_COPY;
};

export interface ExchangeConfig {
  name: string;
  videoUrl: string;
  apiManagementUrl: string;
  managementLinkLabel?: string;
  whitelistIp: string;
  requiresPassphrase?: boolean;
  instructions: {
    title: string;
    steps: string[];
    securityNotes: string[];
  };
  tutorialSteps?: TutorialStep[];
}

export const DEFAULT_WHITELIST_IP = '208.77.244.15';

/**
 * Generate exchange configuration with custom whitelist IP
 */
const createExchangeConfigs = (whitelistIp: string): Record<string, ExchangeConfig> => ({
  bybit: {
    name: 'Bybit',
    videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // TODO: Replace with actual tutorial video
    apiManagementUrl: 'https://www.bybit.com/app/user/api-management',
    whitelistIp: whitelistIp,
    instructions: {
      title: 'Bybit API Key Setup',
      steps: [
        'Go to your Bybit API Management page',
        'Create a new API key with the following permissions:',
        '  System-generated keys',
        '  Unified Trading - Orders, Positions, USDT Derivatives Trading',
      ],
      securityNotes: [
        'Never share your API credentials',
        'The bot will never ask for withdrawal permissions',
      ],
    },
    tutorialSteps: [
      { image: '/tutorials/bybit/1.png', description: 'Go to Bybit API Management page' },
      { image: '/tutorials/bybit/2.png', description: 'Click "Create New Key" and select "System-generated API Keys"' },
      { image: '/tutorials/bybit/3.png', description: 'Set API key name and select "Read-Write" permissions' },
      { image: '/tutorials/bybit/4.png', description: `Check "Unified Trading" permissions` },
      { image: '/tutorials/bybit/5.png', description: 'Pass security validation"' },
      { image: '/tutorials/bybit/6.png', description: 'Copy your API Key and Secret Key' },
      { image: '/tutorials/bybit/7.png', description: 'Enter your API Key and Secret Key in the Trigger and submit' },
    ],
  },
  'bybit-demo': {
    name: 'Bybit Demo',
    videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // TODO: Replace with actual tutorial video
    apiManagementUrl: 'https://bybit.com/app/user/api-management',
    whitelistIp: whitelistIp,
    instructions: {
      title: 'Bybit Demo API Key Setup',
      steps: [
        'Go to your Bybit API Management page and switch to Demo Trading Account',
        'Create a new API key with the following permissions:',
        '  System-generated keys',
        '  Read-Write - Read required to read your positions and write to create them',
        '  Unified Trading - Orders, Positions, USDT Derivatives Trading',
      ],
      securityNotes: [
        'Never share your API credentials',
        'The bot will never ask for withdrawal permissions',
      ],
    },
    tutorialSteps: [
      { image: '/tutorials/bybit/1.png', description: 'Switch to Demo Trading Account and go to API Management' },
      { image: '/tutorials/bybit/2.png', description: 'Click "Create New Key" and select "System-generated API Keys"' },
      { image: '/tutorials/bybit/3.png', description: 'Set API key name and select "Read-Write" permissions' },
      { image: '/tutorials/bybit/4.png', description: 'Leave IP restriction unrestricted (no whitelist required)' },
      { image: '/tutorials/bybit/5.png', description: 'Enable "Unified Trading" permissions for Orders and Positions' },
      { image: '/tutorials/bybit/6.png', description: 'Review your settings and click "Submit"' },
      { image: '/tutorials/bybit/7.png', description: 'Copy your API Key and Secret Key' },
    ],
  },
  binance: {
    name: 'Binance',
    videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // TODO: Replace with actual tutorial video
    apiManagementUrl: 'https://www.binance.com/en/my/settings/api-management',
    whitelistIp: whitelistIp,
    instructions: {
      title: 'Binance API Key Setup',
      steps: [
        'Make sure you have funds in your futures account',
        'Go to your Binance API Management page',
        'Create a new API key with the following permissions:',
        `  IP whitelist: ${whitelistIp} (without it you won't be able to enable futures)`,
        '  Reading',
        '  Enable Futures',
      ],
      securityNotes: [
        'Never share your API credentials',
        'The bot will never ask for withdrawal permissions',
      ],
    },
    tutorialSteps: [
      { image: '/tutorials/binance/1.png', description: 'Go to Binance API Management page' },
      { image: '/tutorials/binance/2.png', description: 'Click "Create API" and choose "System generated"' },
      { image: '/tutorials/binance/3.png', description: 'Give your API key a label/name' },
      { image: '/tutorials/binance/4.png', description: 'Pass security validation' },
      { image: '/tutorials/binance/5.png', description: 'Copy your API Key and Secret Key' },
      { image: '/tutorials/binance/6.png', description: `Restrict access to trusted IPs: ${whitelistIp} and click "Save"` },
      { image: '/tutorials/binance/7.png', description: 'Click "Enable Futures" and then save by clicking "Save" (If you don\'t see it, it means Futures are not supported in your region)' },
      { image: '/tutorials/binance/8.png', description: 'Enter your API Key and Secret Key in the Trigger and submit' },
    ],
  },
  'binance-demo': {
    name: 'Binance Demo',
    videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // TODO: Replace with actual tutorial video
    apiManagementUrl: 'https://testnet.binance.com/en/my/settings/api-management',
    whitelistIp: whitelistIp,
    instructions: {
      title: 'Binance Demo API Key Setup',
      steps: [
        'Make sure you have funds in your futures account',
        'Go to your Binance Testnet API Management page',
        'Create a new API key with the following permissions:',
        `  IP whitelist: ${whitelistIp}`,
        '  Reading',
        '  Enable Futures',
      ],
      securityNotes: [
        'Never share your API credentials',
        'The bot will never ask for withdrawal permissions',
      ],
    },
    tutorialSteps: [
      { image: '/tutorials/binance/1.png', description: 'Go to Binance Testnet API Management page' },
      { image: '/tutorials/binance/2.png', description: 'Click "Create API" and choose "System generated"' },
      { image: '/tutorials/binance/3.png', description: 'Give your API key a label/name' },
      { image: '/tutorials/binance/4.png', description: `Restrict access to trusted IPs: ${whitelistIp}` },
      { image: '/tutorials/binance/5.png', description: 'Enable "Enable Reading" permission' },
      { image: '/tutorials/binance/6.png', description: 'Enable "Enable Futures" permission' },
      { image: '/tutorials/binance/7.png', description: 'Complete verification and submit' },
      { image: '/tutorials/binance/8.png', description: 'Copy your API Key and Secret Key' },
    ],
  },
  gateio: {
    name: 'Gate.io',
    videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // TODO: Replace with actual tutorial video
    apiManagementUrl: 'https://www.gate.com/myaccount/profile/api-key/save',
    whitelistIp: whitelistIp,
    instructions: {
      title: 'Gate.io API Key Setup',
      steps: [
        'Make sure you have funds in your futures account',
        'Go to your Gate.io API Management page',
        'Create a new API key with the following permissions:',
        '  Account type: Trading Account',
        '  API: v4 Key',
        '  Perpetual Futures: Read and write',
        '  Account: Read only',
      ],
      securityNotes: [
        'Never share your API credentials',
        'The bot will never ask for withdrawal permissions',
      ],
    },
    tutorialSteps: [
      { image: '/tutorials/gate/1.png', description: 'Go to Gate.io API Management page' },
      { image: '/tutorials/gate/2.png', description: 'Click "Create API Key" and select "Trading Account" type' },
      { image: '/tutorials/gate/3.png', description: 'Name your API key' },
      { image: '/tutorials/gate/4.png', description: 'Enable "Perpetual Futures" with Read and Write permissions and Account with "Read only" permission' },
      { image: '/tutorials/gate/5.png', description: 'Accept all Risk Reminders and click "I Accept" to continue' },
      { image: '/tutorials/gate/6.png', description: 'Pass security validation' },
      { image: '/tutorials/gate/7.png', description: 'Copy your API Key and Secret Key' },
      { image: '/tutorials/gate/8.png', description: 'Enter your API Key and Secret Key in the Trigger and submit' },
    ],
  },
  'gateio-demo': {
    name: 'Gate.io Demo',
    videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // TODO: Replace with actual tutorial video
    apiManagementUrl: 'https://testnet.gate.com/myaccount/profile/api-key/save',
    whitelistIp: whitelistIp,
    instructions: {
      title: 'Gate.io Demo API Key Setup',
      steps: [
        'Make sure you have funds in your futures account',
        'Go to your Gate.io Testnet API Management page',
        'Create a new API key with the following permissions:',
        '  Account type: Testnet',
        '  API: v4 Key',
        '  Perpetual Futures: Read and write',
        '  Account: Read only',
      ],
      securityNotes: [
        'Never share your API credentials',
        'The bot will never ask for withdrawal permissions',
      ],
    },
    tutorialSteps: [
      { image: '/tutorials/gate/1.png', description: 'Go to Gate.io Testnet API Management page' },
      { image: '/tutorials/gate/2.png', description: 'Click "Create API Key" and select v4 API' },
      { image: '/tutorials/gate/3.png', description: 'Select "Testnet" account type' },
      { image: '/tutorials/gate/4.png', description: 'Enable "Perpetual Futures" with Read and Write permissions' },
      { image: '/tutorials/gate/5.png', description: 'Enable "Account" with Read only permission' },
      { image: '/tutorials/gate/6.png', description: 'Leave IP restriction unrestricted (no whitelist required)' },
      { image: '/tutorials/gate/7.png', description: 'Complete verification and submit' },
      { image: '/tutorials/gate/8.png', description: 'Copy your API Key and Secret Key' },
    ],
  },
  kucoin: {
    name: 'KuCoin',
    videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // TODO: Replace with actual tutorial video
    apiManagementUrl: 'https://www.kucoin.com/en-us/account/api',
    whitelistIp: whitelistIp,
    requiresPassphrase: true,
    instructions: {
      title: 'KuCoin API Key Setup',
      steps: [
        'Go to your KuCoin API Management page',
        'Create a new API key with the following permissions:',
        '  System-generated keys',
      ],
      securityNotes: [
        'Never share your API credentials',
        'The bot will never ask for withdrawal permissions',
      ],
    },
    tutorialSteps: [
      { image: '/tutorials/kucoin/1.png', description: 'Go to KuCoin API Management page and click "Create API"' },
      { image: '/tutorials/kucoin/2.png', description: 'Name your API key, enter your passphrase (you will need it later) ad select "Futures Trading" in API Restrictions' },
      { image: '/tutorials/kucoin/3.png', description: 'Leave IP restriction unrestricted and click "Next"' },
      { image: '/tutorials/kucoin/4.png', description: 'Pass Security Validation' },
      { image: '/tutorials/kucoin/5.png', description: 'Copy your API Key and Secret Key' },
      { image: '/tutorials/kucoin/6.png', description: 'Enter your API Key, Secret Key and Passphrase (from step 2) in the Trigger and submit' },
    ],
  },
  aster: {
    name: 'Aster',
    videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // TODO: Replace with actual tutorial video
    apiManagementUrl: 'https://www.asterdex.com/en/api-wallet',
    managementLinkLabel: 'Open Pro API Wallet Page',
    whitelistIp: whitelistIp,
    requiresPassphrase: true,
    instructions: {
      title: 'Aster Pro API Setup',
      steps: [
        'Open the API wallet page and stay on the Pro API tab',
        'If the page says "Please enable trading first", sign Establish connection and click Connect',
        'Click Authorise New API Wallet. Check Read and Perps trading. Leave Spot trading and Withdrawal off, Days Valid at 365, and IP Restrictions empty',
        'Hover the private key, click Copy Private Key, then click Authorise',
        'Copy the API Wallet Address from the Authorized API Wallets table. The main wallet is the wallet connected at the top of the page (Main account)',
      ],
      securityNotes: [
        'Never share the API wallet private key',
        'Copy the private key before you click Authorise. The table only keeps the address',
      ],
    },
    tutorialSteps: [
      { image: '/tutorials/aster/1.png', description: 'Open Pro API. If you see "Please enable trading first", enable trading before creating a wallet' },
      { image: '/tutorials/aster/2.png', description: 'Sign the Establish connection prompt and click Connect' },
      { image: '/tutorials/aster/3.png', description: 'In Authorise API, check Read and Perps trading. Leave Spot trading and Withdrawal off, Days Valid at 365, and IP Restrictions empty. Copy the private key, then click Authorise' },
      { image: '/tutorials/aster/4.png', description: 'Copy the API Wallet Address from the table. Your main wallet is the connected wallet at the top of the page (Main account)' },
    ],
  },
  hyperliquid: {
    name: 'Hyperliquid',
    videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // TODO: Replace with actual tutorial video
    apiManagementUrl: 'https://app.hyperliquid.xyz/API',
    whitelistIp: whitelistIp,
    requiresPassphrase: true,
    instructions: {
      title: 'Hyperliquid Wallet Setup',
      steps: [
        'Hyperliquid uses two wallets: Account Wallet and API Wallet',
        'Account Wallet: The Ethereum wallet address you used to create your Hyperliquid account',
        'API Wallet: A separate wallet used for trading (created via Hyperliquid app)',
        'Go to Hyperliquid app and create an API wallet',
        'Copy your Account Wallet address (the wallet you used to sign up)',
        'Copy your API Wallet public key (wallet address starting with 0x...)',
        'Copy your API Wallet private key (keep this secure!)',
      ],
      securityNotes: [
        'Never share your private keys',
        'The API Wallet private key is used only for signing trades',
        'Your Account Wallet holds your funds',
      ],
    },
    tutorialSteps: [
      { image: '/tutorials/hyperliquid/1.png', description: 'Go to Hyperliquid app API page and click "Generate' },
      { image: '/tutorials/hyperliquid/2.png', description: 'Name your API wallet and click "Authorize API Wallet"' },
      { image: '/tutorials/hyperliquid/3.png', description: 'Copy your API Wallet public key (0x...) and private key. Click "Max" and then "Authorize"' },
      { image: '/tutorials/hyperliquid/4.png', description: 'Copy your Account Wallet address used for signup' },
      { image: '/tutorials/hyperliquid/5.png', description: 'Enter API Wallet public key, private key and Account Wallet address in the Trigger and submit' },
    ],
  },
});

/**
 * Get exchange configuration with optional custom whitelist IP
 */
export const getExchangeConfig = (
  exchangeCode: string,
  whitelistIp?: string
): ExchangeConfig | null => {
  const ip = whitelistIp || DEFAULT_WHITELIST_IP;
  const configs = createExchangeConfigs(ip);
  return configs[exchangeCode] || null;
};

export const getSupportedExchanges = (): string[] => {
  const configs = createExchangeConfigs(DEFAULT_WHITELIST_IP);
  return Object.keys(configs);
};

